﻿import { useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

export default function BlogHrGlasPage() {
  const { t } = useTranslation("blog-hr-glas");

  const faqItems = t("faq.items", { returnObjects: true }) as Array<{
    q: string;
    a: string;
  }>;

  const voorwaardenItems = t("sections.voorwaarden.items", {
    returnObjects: true,
  }) as Array<{ title: string; text: string }>;

  const steps = t("sections.aanvraag.steps", {
    returnObjects: true,
  }) as Array<{ num: string; title: string; text: string }>;

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: t("schema.headline"),
    description: t("schema.description"),
    author: {
      "@type": "Organization",
      name: t("schema.author"),
      url: "https://ramen-repair-eindhoven.nl/",
    },
    publisher: {
      "@type": "Organization",
      name: "Ramen Repair Eindhoven",
      url: "https://ramen-repair-eindhoven.nl/",
    },
    datePublished: t("schema.datePublished"),
    dateModified: t("schema.dateModified"),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://ramen-repair-eindhoven.nl/blog/subsidie-hr-glas-2026/",
    },
    image: t("hero.image"),
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://ramen-repair-eindhoven.nl/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://ramen-repair-eindhoven.nl/blog/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Subsidie HR++ glas 2026",
        item: "https://ramen-repair-eindhoven.nl/blog/subsidie-hr-glas-2026/",
      },
    ],
  };

  return (
    <>
      <title>{t("meta.title")}</title>
      <meta name="description" content={t("meta.description")} />
      <link
        rel="canonical"
        href="https://ramen-repair-eindhoven.nl/blog/subsidie-hr-glas-2026/"
      />
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      <Header />

      <main>
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="bg-brand-cream border-b border-brand-border">
          <div className="max-w-[1024px] mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 font-geist text-sm text-brand-text/60 flex-wrap">
              <li>
                <a href="/" className="hover:text-brand-text transition-colors">
                  Home
                </a>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <a href="/blog/" className="hover:text-brand-text transition-colors">
                  Blog
                </a>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-brand-text font-medium" aria-current="page">
                Subsidie HR++ glas 2026
              </li>
            </ol>
          </div>
        </nav>

        {/* Hero */}
        <section className="relative bg-brand-cream border-b border-brand-border overflow-hidden">
          <div className="max-w-[1024px] mx-auto px-4 md:px-6 py-10 md:py-16">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-4 md:mb-5">
                  <a
                    href="/blog/"
                    className="inline-flex items-center gap-1.5 font-geist text-sm text-brand-text/50 hover:text-brand-text transition-colors"
                  >
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center text-sm"></i>
                    Blog
                  </a>
                  <span className="text-brand-text/30">·</span>
                  <span className="font-geist text-sm text-brand-text/50">
                    {t("hero.readTime")}
                  </span>
                  <span className="text-brand-text/30">·</span>
                  <span className="font-geist text-sm text-brand-text/50">
                    {t("hero.updated")}
                  </span>
                </div>
                <h1 className="font-geist font-bold text-2xl md:text-4xl text-brand-text mb-4 md:mb-6 leading-tight">
                  {t("hero.h1")}
                </h1>
                <p className="font-geist text-base md:text-lg text-brand-text/70 leading-relaxed max-w-[640px]">
                  {t("hero.subtitle")}
                </p>
              </div>
              <div className="w-full md:w-[380px] lg:w-[440px] flex-shrink-0">
                <div className="rounded-2xl overflow-hidden border border-brand-border">
                  <img
                    src={t("hero.image")}
                    alt="HR++ glas subsidie 2026 energiezuinige ramen"
                    className="w-full h-48 md:h-64 object-cover"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <div className="max-w-[1024px] mx-auto px-4 md:px-6 py-10 md:py-16">
          {/* H2: Wat is HR++ glas */}
          <section id="wat-is-hr-glas" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.watIs.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.watIs.p1")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.watIs.p2")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed">
              {t("sections.watIs.p3")}
            </p>
          </section>

          {/* H2: Subsidiebedragen */}
          <section id="subsidiebedragen" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.bedragen.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.bedragen.intro")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.bedragen.p1")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.bedragen.p2")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
              {t("sections.bedragen.p3")}
            </p>
            <a
              href="/blog/isde-subsidie-2026/"
              className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text hover:text-brand-yellow transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
              {t("sections.bedragen.linkIsde.text")}
            </a>
          </section>

          {/* H2: HR++ vs HR+++ */}
          <section id="hr-vs-triple" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.triple.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.triple.p1")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.triple.p2")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed">
              {t("sections.triple.p3")}
            </p>
          </section>

          {/* H2: Voorwaarden */}
          <section id="voorwaarden" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.voorwaarden.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
              {t("sections.voorwaarden.intro")}
            </p>
            <div className="flex flex-col gap-4 md:gap-5">
              {voorwaardenItems.map((item, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-brand-border bg-white p-4 md:p-5"
                >
                  <h3 className="font-geist font-semibold text-sm md:text-base text-brand-text mb-1 md:mb-2">
                    {item.title}
                  </h3>
                  <p className="font-geist text-sm text-brand-text/70 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* H2: Aanvraag */}
          <section id="aanvraag" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.aanvraag.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
              {t("sections.aanvraag.intro")}
            </p>
            <div className="flex flex-col gap-4 md:gap-5">
              {steps.map((step, idx) => (
                <div
                  key={idx}
                  className="flex gap-4 md:gap-5 items-start"
                >
                  <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#0C2740] flex items-center justify-center">
                    <span className="font-geist-mono text-sm md:text-base font-bold text-[#FFD600]">
                      {step.num}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-geist font-semibold text-sm md:text-base text-brand-text mb-1">
                      {step.title}
                    </h3>
                    <p className="font-geist text-sm text-brand-text/70 leading-relaxed">
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 md:mt-6">
              <a
                href="/blog/isde-subsidie-2026/"
                className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text hover:text-brand-yellow transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
                {t("sections.aanvraag.linkIsde.text")}
              </a>
            </div>
          </section>

          {/* H2: Eindhoven */}
          <section id="eindhoven" className="mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-4 md:mb-5">
              {t("sections.eindhoven.h2")}
            </h2>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.eindhoven.p1")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-3 md:mb-4">
              {t("sections.eindhoven.p2")}
            </p>
            <p className="font-geist text-sm md:text-base text-brand-text/70 leading-relaxed mb-5 md:mb-6">
              {t("sections.eindhoven.p3")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="/glas-vervangen/"
                className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text hover:text-brand-yellow transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
                {t("sections.eindhoven.linkGlas.text")}
              </a>
              <a
                href="/kozijn-reparatie/"
                className="inline-flex items-center gap-1.5 font-geist text-sm font-medium text-brand-text hover:text-brand-yellow transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-sm"></i>
                {t("sections.eindhoven.linkKozijn.text")}
              </a>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="pt-4 md:pt-6 mb-10 md:mb-14">
            <h2 className="font-geist font-bold text-xl md:text-2xl text-brand-text mb-5 md:mb-6">
              {t("faq.h2")}
            </h2>
            <div className="flex flex-col gap-3 md:gap-4">
              {faqItems.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div
                    key={idx}
                    className={`border rounded-xl overflow-hidden transition-colors ${
                      isOpen
                        ? "border-brand-yellow bg-[#FFF9E0]/30"
                        : "border-brand-border bg-white"
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="w-full flex items-start justify-between gap-3 p-4 md:p-5 text-left cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span className="font-geist font-semibold text-sm md:text-base text-brand-text leading-snug">
                        {item.q}
                      </span>
                      <span className="flex-shrink-0 w-6 h-6 rounded-full border border-brand-text/20 flex items-center justify-center mt-0.5">
                        <i
                          className={`ri-${
                            isOpen ? "subtract" : "add"
                          }-line w-4 h-4 flex items-center justify-center text-sm text-brand-text/60`}
                        ></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="font-geist text-sm text-brand-text/70 leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <div className="bg-[#0C2740] rounded-2xl p-6 md:p-10 text-white mb-10 md:mb-14">
            <h3 className="font-geist font-bold text-lg md:text-xl text-white mb-3">
              {t("cta.text")}
            </h3>
            <a
              href="tel:+31407987470"
              className="inline-flex items-center gap-2 font-geist font-bold text-sm bg-[#FFD600] text-[#0C2740] px-6 py-3 rounded-full hover:bg-[#FFD600]/90 transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center text-sm"></i>
              {t("cta.phone")}
            </a>
          </div>

          {/* Disclaimer */}
          <div className="border-t border-brand-border pt-6 md:pt-8">
            <p className="font-geist text-xs md:text-sm text-brand-text/40 italic leading-relaxed">
              {t("disclaimer")} Laatst bijgewerkt mei 2026.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}